import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import Logo from '../../components/Logo';
import { Eye, EyeOff, LogIn } from 'lucide-react';

const AdminLogin = () => {
    const { login, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const { register, handleSubmit, formState: { errors } } = useForm();

    const from = location.state?.from?.pathname || '/admin/dashboard';

    useEffect(() => {
        if (isAuthenticated) {
            navigate(from, { replace: true });
        }
    }, [isAuthenticated, navigate, from]);

    const onSubmit = async (data) => {
        setIsLoading(true);

        try {
            const result = await login(data.email, data.password);

            if (result.success) {
                toast.success(`Welcome back, ${result.admin.name}!`);
                navigate(from, { replace: true });
            } else {
                toast.error(result.error);
            }
        } catch (error) {
            toast.error('An unexpected error occurred');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-background">
                <div className="login-card">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className="login-header">
                            <Logo size="large" />
                            <h1>Admin Portal</h1>
                            <p>Distance Education Assessment System</p>
                        </div>

                        <form onSubmit={handleSubmit(onSubmit)} className="login-form">
                            <div className="form-group">
                                <label htmlFor="email" className="form-label">Email Address</label>
                                <input
                                    id="email"
                                    type="email"
                                    className={`form-control ${errors.email ? 'error' : ''}`}
                                    placeholder="Enter your email"
                                    {...register('email', {
                                        required: 'Email is required',
                                        pattern: {
                                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                            message: 'Invalid email address'
                                        }
                                    })}
                                />
                                {errors.email && (
                                    <div className="error-message">{errors.email.message}</div>
                                )}
                            </div>

                            <div className="form-group">
                                <label htmlFor="password" className="form-label">Password</label>
                                <div className="password-input">
                                    <input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        className={`form-control ${errors.password ? 'error' : ''}`}
                                        placeholder="Enter your password"
                                        {...register('password', {
                                            required: 'Password is required',
                                            minLength: {
                                                value: 6,
                                                message: 'Password must be at least 6 characters'
                                            }
                                        })}
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                    </button>
                                </div>
                                {errors.password && (
                                    <div className="error-message">{errors.password.message}</div>
                                )}
                            </div>

                            <button
                                type="submit"
                                className="btn btn-primary btn-lg w-100"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <div className="loading-spinner" style={{ width: '20px', height: '20px' }} />
                                ) : (
                                    <>
                                        <LogIn size={20} />
                                        Sign In
                                    </>
                                )}
                            </button>
                        </form>

                        <div className="login-footer">
                            <p className="text-muted">
                                <strong>⚠️ INTERNAL SYSTEM</strong><br />
                                <strong>AUTHORIZED ACCESS ONLY</strong><br />
                                Unauthorized access is prohibited and may be subject to legal action.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </div>

            <style jsx>{`
        .login-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, var(--primary-bg) 0%, #d6e7ff 100%);
          padding: 20px;
        }

        .login-background {
          width: 100%;
          max-width: 420px;
        }

        .login-card {
          background: var(--white);
          padding: 40px;
          border-radius: var(--border-radius-lg);
          box-shadow: 0 20px 40px rgba(0, 89, 255, 0.1);
          border: 1px solid rgba(0, 89, 255, 0.1);
        }

        .login-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .login-header h1 {
          font-size: 28px;
          font-weight: 700;
          color: var(--primary-accent);
          margin: 20px 0 8px 0;
        }

        .login-header p {
          color: var(--text-secondary);
          font-size: 16px;
          margin: 0;
        }

        .login-form {
          margin-bottom: 24px;
        }

        .password-input {
          position: relative;
        }

        .password-toggle {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: var(--transition);
        }

        .password-toggle:hover {
          color: var(--primary-accent);
          background-color: var(--gray-100);
        }

        .login-footer {
          text-align: center;
          padding-top: 24px;
          border-top: 1px solid var(--gray-200);
        }

        .login-footer p {
          font-size: 14px;
          line-height: 1.5;
        }

        @media (max-width: 480px) {
          .login-card {
            padding: 24px;
          }

          .login-header h1 {
            font-size: 24px;
          }
        }
      `}</style>
        </div>
    );
};

export default AdminLogin;

